import React, { useState, DragEvent, ChangeEvent } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  inputStyle,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import LoadingOverlay from "../components/LoadingOverlay";
import { uploadNotificationBatch } from "../services/NotificationService";
import Toast from "../components/Toast";

const UploadBatchNotifications: React.FC = () => {
  const [mode, setMode] = useState<"excel" | "json">("excel");
  const [file, setFile] = useState<File | null>(null);
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [showToast, setShowToast] = useState<boolean>(false);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setMode(newValue as "excel" | "json");
    // Reset states
    setFile(null);
    setJsonInput("");
    setJsonError(null);
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
    }
  };

  const handleJsonChange = (e: ChangeEvent<HTMLInputElement>) => {
    setJsonInput(e.target.value);
    try {
      JSON.parse(e.target.value);
      setJsonError(null);
    } catch (err) {
      setJsonError("Invalid JSON");
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (mode === "excel") {
      if (!file) {
        setLoading(false);
        setMessage("Please select an Excel file.");
        setSeverity("error");
        setShowToast(true);
        return;
      }

      console.log("Submitting Excel file:", file);
      await uploadNotificationBatch({ file })
        .then(() => {
          setMessage("Successfully uploaded batch notification.");
          setSeverity("success");
          setShowToast(true);
          setLoading(false);
        })
        .catch((e) => {
          setMessage("Failed to submit file");
          setSeverity("error");
          setShowToast(true);
          setLoading(false);
        });
    } else {
      if (jsonError) {
        setLoading(false);
        setMessage("Fix JSON errors first.");
        setSeverity("error");
        setShowToast(true);
      }
      try {
        const parsed = JSON.parse(jsonInput);
        console.log("Submitting parsed JSON:", parsed);
        await uploadNotificationBatch(parsed);
        setMessage("Successfully uploaded batch notification.");
        setSeverity("success");
        setShowToast(true);
      } catch {
        setMessage("Failed to upload notification");
        setSeverity("error");
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Paper
      elevation={4}
      sx={{
        padding: 4,
        maxWidth: 850,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Upload Batch Notifications
      </Typography>

      <Tabs
        value={mode}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          minHeight: "unset",
          "& .MuiTabs-indicator": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: "transparent",
            height: "3px",
          },
          "& .MuiTabs-indicatorSpan": {
            maxWidth: "50%",
            width: "100%",
            backgroundColor: "primary.main",
          },
        }}
        TabIndicatorProps={{
          children: <span className="MuiTabs-indicatorSpan" />,
        }}
      >
        <Tab
          label="Excel File"
          value="excel"
          sx={{
            textTransform: "none",
            minWidth: "auto",
            minHeight: "unset",
            px: 2,
          }}
        />
        <Tab
          label="JSON Input"
          value="json"
          sx={{
            textTransform: "none",
            minWidth: "auto",
            minHeight: "unset",
            px: 2,
          }}
        />
      </Tabs>

      <LoadingOverlay loading={loading} />
      <Toast
        message={message}
        severity={severity}
        open={showToast}
        onClose={() => setShowToast(false)}
      />

      {mode === "excel" && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            border: "2px dashed #ccc",
            textAlign: "center",
            backgroundColor: "#fafafa",
            cursor: "pointer",
          }}
          onDrop={handleFileDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <UploadFileIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="body1" mb={2}>
            Drag & drop your Excel file here, or click to select file for upload
          </Typography>
          <Button
            variant="outlined"
            component="label"
            sx={[primaryBtnStyleOutline, { width: 200, margin: "0 auto" }]}
          >
            Select File
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleFileSelect}
            />
          </Button>

          {file && (
            <Typography mt={2}>
              Selected File: <strong>{file.name}</strong>
            </Typography>
          )}
        </Paper>
      )}

      {mode === "json" && (
        <Box>
          <TextField
            label="Paste JSON Here"
            placeholder='[{"title": "...", "body": "..."}]'
            fullWidth
            multiline
            minRows={10}
            value={jsonInput}
            onChange={handleJsonChange}
            error={!!jsonError}
            helperText={
              jsonError || "Provide a valid JSON array of notifications."
            }
            sx={[inputStyle(), { height: "auto" }]}
          />
        </Box>
      )}

      <Grid container justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            (mode === "excel" ? !file : !!jsonError || !jsonInput) || loading
          }
          sx={primaryBtnStyle}
        >
          Submit
        </Button>
      </Grid>
    </Paper>
  );
};

export default UploadBatchNotifications;
